import {gql} from '@apollo/client';
import {Stack} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {assert} from 'Asserts';
import {MinimalStorableSummaryWithSchemaId} from 'constants/SampleConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {BioentitySummary} from 'graphql-client/declarative-translations/external/RESTTypes';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {DESTINATION_WELL_COLUMN_NAME} from 'notebook/integrations/samples/TransferStructuredTableAdapter';
import fetchContainersFromPlateWellPositions, {
  FetchedContainerInfo,
} from 'notebook/structured-tables/util/fetchContainersFromPlateWellPositions';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {findMatchingDestinationColumnIndices} from 'processes/tables/column-mapping/findMatchingDestinationColumnIndex';
import getMatchingColumnConstraints from 'processes/tables/column-mapping/getMatchingColumnConstraints';
import {ProcessTaskStructuredTable} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import CenteredHorizontalThrobber from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';

import {ColumnSummaryQuery, ColumnSummaryQueryVariables} from './__generated/ColumnSummaryQuery';
import MultiColumnMappingModalContent from './select-task-column-mappings-modal/MultiColumnMappingModalContent';
import {useSelectTaskColumnMappingsModalStyles} from './select-task-column-mappings-modal/selectTaskColumnMappingsModalStyles';
import type {
  ColumnMappingModalSubmitControls,
  ColumnSummary,
  ProcessTaskSchema,
  QueryingSelectTaskColumnMappingsModalProps,
  SelectTargetColumnModalProps,
  TaskColumnMappingInfo,
} from './select-task-column-mappings-modal/selectTaskColumnMappingsModalTypes';
import {SelectTaskColumnMappingsModalCreationType} from './select-task-column-mappings-modal/selectTaskColumnMappingsModalTypes';

export type {
  ColumnSummary,
  ProcessTaskSchema,
  QueryingSelectTaskColumnMappingsModalProps,
  SelectTargetColumnModalProps,
  SelectTargetColumnModalSubmitResult,
  TaskColumnMappingInfo,
  TaskColumnMappingInfoBase,
  TaskColumnMappingInfoWithIds,
  TaskColumnMappingInfoWithValues,
} from './select-task-column-mappings-modal/selectTaskColumnMappingsModalTypes';
export {SelectTaskColumnMappingsModalCreationType};

export const COLUMN_SUMMARY_QUERY = gql`
  query ColumnSummaryQuery($bioentityIds: [StrictString!]!, $storableIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    bioentities(ids: $bioentityIds) {
      ...BioentitySummaryOrInaccessibleGraphQL
    }
    storables(ids: $storableIds) {
      ... on StorableInterface {
        ...StorableSummaryGraphQL
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('BioentitySummaryOrInaccessible').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('StorableSummary').fragment}
`;

type SummariesById = Record<string, BioentitySummary | MinimalStorableSummaryWithSchemaId>;
type ColumnValues = Array<BioentitySummary> | Array<MinimalStorableSummaryWithSchemaId>;

function getSelectableDestinationColumns(
  sourceItems: ColumnValues,
  taskTable: ProcessTaskStructuredTable,
  taskSchema: ProcessTaskSchema,
  shouldFetchPlateInfo?: boolean,
  fetchedContainerInfo?: FetchedContainerInfo | null
): Array<ColumnSummary> {
  assert(sourceItems.length > 0, 'Source column must have at least 1 value');
  let sourceTablePrimaryColumnSummary = getMatchingColumnConstraints(sourceItems);
  if (shouldFetchPlateInfo) {
    sourceTablePrimaryColumnSummary = {
      ...sourceTablePrimaryColumnSummary,
      schemaIdConstraint: fetchedContainerInfo?.schemaId ?? null,
    };
  }
  const destinationColumnIndices = findMatchingDestinationColumnIndices(
    sourceTablePrimaryColumnSummary,
    taskSchema,
    taskTable
  );
  return destinationColumnIndices.map((destinationColumnIndex) => {
    const column = taskTable.columns[destinationColumnIndex];
    return {
      columnName: column.name,
      destinationColumnIndex,
    };
  });
}

function isColumnDestinationWellForTransfer(column: TaskColumnMappingInfo): boolean {
  return !column.hasValues && column.shouldFetchPlateInfo;
}

function getSelectedSourceItems(
  sourceColumn: TaskColumnMappingInfo,
  summariesById: SummariesById
): {selectedSourceItems: ColumnValues; shouldFetchPlateInfo?: boolean} {
  let selectedSourceItems: ColumnValues = [];
  let shouldFetchPlateInfo: boolean | undefined;
  if (sourceColumn.hasValues) {
    selectedSourceItems = sourceColumn.columnValues;
  } else {
    selectedSourceItems = sourceColumn.columnIds.map((id) => summariesById[id]) as
      | Array<BioentitySummary>
      | Array<MinimalStorableSummaryWithSchemaId>;
    shouldFetchPlateInfo = sourceColumn.shouldFetchPlateInfo;
  }
  return {selectedSourceItems, shouldFetchPlateInfo};
}

function getSourceColumnDisplayName(column: TaskColumnMappingInfo): string {
  return isColumnDestinationWellForTransfer(column) ? DESTINATION_WELL_COLUMN_NAME : column.columnName;
}

/**
 * Returns each source column's currently-selectable destination task fields. A source whose items
 * are not yet resolved (`selectedSourceItems` is empty) contributes an empty array.
 */
function getSelectableDestinationsBySourceColumn(
  sourceColumns: Array<TaskColumnMappingInfo>,
  summariesById: SummariesById,
  taskTable: ProcessTaskStructuredTable,
  taskSchema: ProcessTaskSchema,
  fetchedContainerInfo: FetchedContainerInfo | null
): Array<Array<ColumnSummary>> {
  return sourceColumns.map((col) => {
    const {selectedSourceItems, shouldFetchPlateInfo} = getSelectedSourceItems(col, summariesById);
    const resolvedItems = selectedSourceItems.filter(
      (item): item is BioentitySummary | MinimalStorableSummaryWithSchemaId => item != null
    );
    if (resolvedItems.length === 0) {
      return [];
    }
    return getSelectableDestinationColumns(
      resolvedItems as ColumnValues,
      taskTable,
      taskSchema,
      shouldFetchPlateInfo,
      fetchedContainerInfo
    );
  });
}

export function SelectTaskColumnMappingsModal({
  sourceColumns,
  taskSchema,
  taskTable,
  selectorsById,
  summariesById,
  fetchedContainerInfo,
  creationType,
  onRequestClose,
  onRequestSubmit,
}: SelectTargetColumnModalProps): JSX.Element {
  const {classes} = useSelectTaskColumnMappingsModalStyles();

  const selectableBySource = useMemo(
    () =>
      getSelectableDestinationsBySourceColumn(
        sourceColumns,
        summariesById,
        taskTable,
        taskSchema,
        fetchedContainerInfo
      ),
    [sourceColumns, summariesById, taskTable, taskSchema, fetchedContainerInfo]
  );

  const [submitControls, setSubmitControls] = useState<ColumnMappingModalSubmitControls | null>(null);

  const onSubmitControlsChange = useCallback((controls: ColumnMappingModalSubmitControls) => {
    setSubmitControls(controls);
  }, []);

  const multiTableIntroText =
    creationType === SelectTaskColumnMappingsModalCreationType.PROCESS
      ? "Choose columns from this table to auto-populate task fields. You'll be able to review the tasks before submitting them."
      : "Choose columns from this table to auto-populate request fields. You'll be able to review this information before submitting it.";
  const creationTypeText =
    creationType === SelectTaskColumnMappingsModalCreationType.PROCESS ? 'tasks' : 'request';
  const fieldText =
    creationType === SelectTaskColumnMappingsModalCreationType.PROCESS ? 'task field' : 'field';

  const modalTitle = `Map columns to create ${creationTypeText}`;

  const handleSubmit = useCallback((): void => {
    assert(submitControls != null, 'Submit controls must be set before submit');
    submitControls.handleSubmit();
  }, [submitControls]);

  const submitButtonText = submitControls?.submitButtonText ?? 'Map column';
  const mapSubmitDisabled = submitControls?.mapSubmitDisabled ?? true;

  return (
    <Modal
      title={modalTitle}
      buttons={[
        {
          text: 'Continue without mapping',
          onClick: () => {
            onRequestSubmit({
              taskTable: null,
              shouldRunCreateModal: true,
            });
          },
        },
        {
          text: submitButtonText,
          submit: true,
          disabled: mapSubmitDisabled,
        },
      ]}
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
    >
      <Stack spacing={0} mb="sm">
        <span className={classes.schemaText}>{`Creating ${creationTypeText}: ${taskSchema.name}`}</span>
      </Stack>
      <MultiColumnMappingModalContent
        sourceColumns={sourceColumns}
        taskSchema={taskSchema}
        taskTable={taskTable}
        summariesById={summariesById}
        fetchedContainerInfo={fetchedContainerInfo}
        creationType={creationType}
        selectableBySource={selectableBySource}
        onRequestSubmit={onRequestSubmit}
        getSourceColumnDisplayName={getSourceColumnDisplayName}
        isColumnDestinationWellForTransfer={isColumnDestinationWellForTransfer}
        getSelectedSourceItems={getSelectedSourceItems}
        onSubmitControlsChange={onSubmitControlsChange}
        fieldText={fieldText}
        multiTableIntroText={multiTableIntroText}
      />
    </Modal>
  );
}

export default function QueryingSelectTaskColumnMappingsModal(
  props: QueryingSelectTaskColumnMappingsModalProps
): JSX.Element | null {
  const bioentityIds = props.sourceColumns
    .filter((column) => !column.hasValues && column.columnType === BaseTypedValueType.BIOENTITY)
    .map((column) => (!column.hasValues ? column.columnIds : []))
    .flat();
  const storableIds = props.sourceColumns
    .filter((column) => !column.hasValues && column.columnType === BaseTypedValueType.STORABLES)
    .map((column) => (!column.hasValues ? column.columnIds : []))
    .flat();
  const shouldFetchContainerInfo =
    props.structuredTableAdapter != null &&
    props.structuredTable != null &&
    props.sourceColumns.some((column) => isColumnDestinationWellForTransfer(column));
  const result = useSkippableBenchlingQuery<ColumnSummaryQuery, ColumnSummaryQueryVariables>(
    COLUMN_SUMMARY_QUERY,
    {
      variables: {
        bioentityIds,
        storableIds,
      },
      skip: bioentityIds.length === 0 && storableIds.length === 0,
    }
  );
  const [fetchedContainerInfo, setFetchedContainerInfo] = useState<FetchedContainerInfo | null>(null);
  const [isFetchingContainerInfo, setIsFetchingContainerInfo] = useState(false);

  useEffect(() => {
    const fetchContainerInfo = async (): Promise<void> => {
      if (shouldFetchContainerInfo) {
        setIsFetchingContainerInfo(true);
        const containersFromPlateWellPositions = await fetchContainersFromPlateWellPositions(
          props.structuredTableAdapter!.getSampleInfosForRequest(props.structuredTable!, props.computedTable)
        );
        setIsFetchingContainerInfo(false);
        setFetchedContainerInfo(containersFromPlateWellPositions);
      }
    };
    fetchContainerInfo();
  }, [props.structuredTableAdapter, props.structuredTable, props.computedTable, shouldFetchContainerInfo]);

  if (result.type === QueryResultType.PENDING || (shouldFetchContainerInfo && isFetchingContainerInfo)) {
    return <CenteredHorizontalThrobber />;
  } else if (result.type === QueryResultType.SUCCESS) {
    const summariesById: SummariesById = {};
    result.data.bioentities.forEach((bioentity) => {
      if (!isInaccessible(bioentity)) {
        summariesById[bioentity.id] = graphQLToREST('BioentitySummary', bioentity);
      }
    });
    result.data.storables.forEach((storable) => {
      if (!isInaccessible(storable)) {
        summariesById[storable.id] = storable;
      }
    });
    let sourceColumns = props.sourceColumns;
    if (shouldFetchContainerInfo && !isFetchingContainerInfo && fetchedContainerInfo == null) {
      sourceColumns = sourceColumns.filter((column) => !isColumnDestinationWellForTransfer(column));
    }
    return (
      <SelectTaskColumnMappingsModal
        {...props}
        sourceColumns={sourceColumns}
        summariesById={summariesById}
        fetchedContainerInfo={fetchedContainerInfo}
      />
    );
  } else if (result.type === QueryResultType.SKIPPED) {
    return <SelectTaskColumnMappingsModal {...props} summariesById={{}} fetchedContainerInfo={null} />;
  } else {
    return null;
  }
}
